/* @flow */
import React, { Component } from 'react'
import sizeMe from 'react-sizeme'

import './App.css'
import List from './List'
import Filters from './Filters'
import ChartContainer from './ChartContainer'
import ScrollerUp from './ScrollerUp'
import Loader from './Loader'
import { getFunds, getFundsData } from './server'
import { getMoment, wrongDateMsg, formatDate, readUrl, setLanguage, setDateRange } from './globals'
import { createId, mergesort } from './utils'

import type { FundsBack, FundData } from './server'
import type { MultiselectOption } from './MultiSelect'
import labels from "./labels";

export type UrlComponents = {
  apiUrl: string,
  date: string,
  product: string,
  owu: string,
  appendix: string,
  id: string
}

export type Fund = {
  id: string,
  name: string,
  category: string,
  categoryShort: ?string,
  currency: string,
  manager: ?string,
  managerShort: ?string,
  code: ?string,
  uri: string,
  valuation: number,
  lastValuationDate: Date,
  categoryOrder: ?number,
  segmentOrder: ?number,
  groupOrder: ?number,
  fundOrder: ?number,
  recent: ?number,
  d: ?number,
  m: ?number,
  q: ?number,
  hy: ?number,
  y: ?number,
  y3: ?number,
  y5: ?number,
  ytd: ?number,
  max: ?number,
  risk: ?number,
  sfdrLevel: ?number
}

export type Funds = Array<Fund>

type Value = {
  date: Date,
  valuation: number
}

export type Values = Array<Value>

export type Info = {
  id: string,
  name: string,
  values: Array<Value>,
  sfdrLevel: ?number
}

export type Data = Array<Info>

export type ActiveFilters = {
  search: string,
  category: Array<string>,
  currency: Array<string>,
  risk: Array<string>
}

export type Sorter = 'checked' | 'name' | 'code' | 'lastValuationDate' | 'valuation' | 'recentChange' | 'd' | 'm' | 'q' | 'y' | 'y3' | 'y5' | 'max' | 'risk'

export type Lang = 'pl' | 'en'

type Display = {
  filters?: boolean,
  fundList?: boolean,
  chart?: boolean,
  buttonGroup?: boolean,
  legend?: boolean
}

export type Initial = {
  funds?: Array<string>,
  activeFilters?: {
    category?: Array<string>,
    currency?: Array<string>,
    risk?: Array<string>
  },
  dateRange?: [string, string],
  limit?: number,
  urlTemplate?: string,
  lang?: Lang,
  apiUrl: string,
  date?: string,
  product?: string,
  owu?: string,
  appendix?: string,
  id?: string
}

export type Mode = 'ufk' | 'vienna' | 'compensa'

export type State = {
  funds: Funds,
  data: Data,
  categories: Array<MultiselectOption>,
  currencies: Array<MultiselectOption>,
  risks: Array<MultiselectOption>,
  activeFilters: ActiveFilters,
  asc: boolean,
  sorter: ?Sorter,
  lang: Lang,
  display: Display,
  grouped: 0 | 1,
  date: string,
  loading: boolean
}

export type Props = {
  initial: Initial,
  display?: Display,
  mode?: Mode,
  size: {
    width: number
  }
}

class App extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    const { activeFilters = {}, dateRange, lang, limit = 15, urlTemplate, apiUrl, product = '', owu = '', appendix = '', id = '', date = '' } = props.initial || {}
    const { display = {} } = props
    const { mode = 'ufk' } = props
    const { grouped = 1 } = props
    this.state = {
      funds: [],
      data: [],
      categories: [],
      currencies: [],
      risks: [],
      activeFilters: {
        search: '',
        category: [],
        currency: [],
        risk: [],
        ...activeFilters
      },
      asc: false,
      sorter: null,
      lang: setLanguage(lang),
      display,
      grouped: grouped,
      date,
      loading: false
    }
    this.mode = mode
    this.urlTemplate = readUrl(urlTemplate)
    this.limit = limit
    this.urlComponents = {apiUrl: readUrl(apiUrl), date, product, owu, appendix, id }
    this.dateRange = setDateRange(dateRange)
  }
  mode: Mode
  dateRange: ?[Date, Date]
  limit: number
  urlTemplate: string
  urlComponents: UrlComponents
  appId: string = createId('ac-app-root')

  componentDidMount () {
    this.setState({ loading: true })
    getFunds(this.composeUrl(this.urlComponents), (fundsBack: FundsBack) => {
      const funds: Funds = this.parseFunds(fundsBack)
      const date = this.maxDate(fundsBack)
      this.setState({
        funds,
        date,
        loading: false,
        ...this.initializeFiltersOptions(funds)
      }, () => {
        const { funds: initFunds = [] } = this.props.initial || {}
        if (initFunds.length === 0 && this.props.mode === 'ufk' && !this.props.initial.product && !this.props.initial.owu && !this.props.initial.appendix){
          this.addData(
              funds
                  .filter(fnd => fnd.managerShort === 'Aegon AM')
                  .filter(fnd => !!fnd.code && fnd.code.startsWith('AEG'))
                  .map(fnd => fnd.id)
          );
        } else {
          this.addData(initFunds)
        }
      })
    })
  }

  composeUrl = ({ apiUrl, product, owu, appendix, id, date }: UrlComponents, suffix: string = 'funds'): string => {
    const queryParams = [{key: 'id', value: id}, {key: 'date', value: date}].filter(({value}) => value).map(({key, value}) => `${key}=${value}`).join('&')
    return `${apiUrl}${suffix}${product && `/${product}`}${owu && `/${owu}`}${appendix && `/${appendix}`}${queryParams ? `?${queryParams}` : '' }`
  }

  parseFunds = (fundsBack: FundsBack): Funds =>
    fundsBack.map(({ id, name, category, category_short, currency, manager, manager_short, fund_code, risk_level, uri, pricing: { valuation, RECENT, D, M, Q, HY, Y, Y3, Y5, YTD, MAX }, date, sfdr_level, category_order, segment_order, group_order, fund_order }) => ({
      id,
      name,
      category,
      categoryShort: category_short,
      currency,
      manager,
      managerShort: manager_short,
      code: fund_code,
      risk: risk_level,
      uri,
      valuation,
      lastValuationDate: date,
      recent: this.parseChange(RECENT),
      d: this.parseChange(D),
      m: this.parseChange(M),
      q: this.parseChange(Q),
      hy: this.parseChange(HY),
      y: this.parseChange(Y),
      y3: this.parseChange(Y3),
      y5: this.parseChange(Y5),
      ytd: this.parseChange(YTD),
      max: this.parseChange(MAX),
      sfdrLevel: sfdr_level,
      categoryOrder: category_order,
      segmentOrder: segment_order,
      fundOrder: fund_order,
      groupOrder: group_order
    }))

  changeDate = (selDate: Date) => {
    this.setState({ loading: true })
    getFunds(this.composeUrl({...this.urlComponents, date: formatDate(selDate) }), (fundsBack: FundsBack) => {
      const { grouped, sorter, asc } = this.state
      const funds: Funds = this.parseFunds(fundsBack)
      sorter && mergesort(funds, this.compare(sorter, asc))
      grouped && mergesort(funds, this.compare('category', true))
      const date = this.maxDate(fundsBack)
      this.setState({ funds, date, loading: false })
    })
  }

  maxDate = (fundsBack: FundsBack): string => {
    let max = ''
    for (let { date } of fundsBack) {
      date > max && (max = date)
    }
    return max
  }

  parseChange = (n: number): ?number => n === null || n === undefined ? null : n

  saveFilterChanges = (name: string) => (value: Array<string>) => {
    this.setState({
      activeFilters: {
        ...this.state.activeFilters,
        [name]: [...value]
      }
    })
  }

  saveSearchInput = ({ currentTarget: { value } }: SyntheticEvent<InputEvent>) => {
    this.setState({
      activeFilters: {
        ...this.state.activeFilters,
        search: value
      }
    })
  }
  clearSearchInput = (event: SyntheticEvent<>) => {
    this.setState({
      activeFilters: {
        ...this.state.activeFilters,
        search: ''
      }
    })
  }

  initializeFiltersOptions (funds: Funds) {
    const { lang } = this.state
    const { mode } = this.props
    const [categorySet, currencySet, riskSet] = [new Set(), new Set(), new Set()]
    const categories: Array<MultiselectOption> = []
    const currencies: Array<MultiselectOption> = []
    const risks: Array<MultiselectOption> = []
    const addOption = (set, arr, name, text) => {
      if (!set.has(name)) {
        arr.push({ key: name, value: name, text: text })
        set.add(name)
      }
    }
    funds.forEach(({ category, risk, currency }) => {
      addOption(categorySet, categories, category, category)
      addOption(currencySet, currencies, currency, currency)
      addOption(riskSet, risks, risk, labels[lang][mode + 'RiskLevel' + risk])
    })

    const compare = ({value: a}, {value: b}) => a.toLowerCase() < b.toLowerCase() ? -1 : 1
    const compareNumber = ({value: a}, {value: b}) => a < b ? -1 : 1

    categories.sort(compare)
    currencies.sort(compare)
    risks.sort(compareNumber)
    return { categories, currencies, risks }
  }

  addData = (ids: Array<string>, callback?: () => void) => {
    const { funds } = this.state
    const { apiUrl } = this.urlComponents
    getFundsData(`${apiUrl}fund/`, ids, (res: Array<FundData>) => {
      const newData: Data = []
      for (let i = 0; i < res.length; i++) {
        const id = ids[i]
        const fundsData = res[i]
        const fundData = { id, values: [] }
        for (let j = 0; j < fundsData.length; j++) {
          const [dateString, valuation] = fundsData[j]
          const mom = getMoment(dateString)
          if (!mom.isValid()) {
            console.error(wrongDateMsg(dateString))
            return
          }
          fundData.values.push({ date: mom.toDate(), valuation})
        }
        const { name, sfdrLevel } = funds.find((fund) => fund.id === id) || { name: '', sfdrLevel: 0 }
        newData.push({id, name, sfdrLevel, ...fundData })
      }
      this.setState(({ data }) => ({ data: [...data, ...newData] }))
      callback && callback()
    }, () => {
      this.setState(({ data }) => ({ data }))
      callback && callback()
    })
  }

  removeData = (ids: Set<string>) => {
    this.setState(({ data }) => ({
      data: data.filter(({ id }) => !ids.has(id))
    }))
  }

  filterFunds = (): {funds: Funds, selected: Set<string>} => {
    const { activeFilters : { search, category, currency, risk }, funds, data } = this.state
    const [ categorySet, currencySet, riskSet, selected ] = [ new Set(category), new Set(currency) , new Set(risk), new Set(data.map(({ id }) => id)) ]
    const filteredFunds = funds.filter(({ id, name, category, risk, currency, code }) => {
      // Do not remove funds from a list that are already selected
      if (selected.has(id)) {
        return true
      }
      const searchName = name.toLowerCase().indexOf(search.toLowerCase()) !== -1
      const searchCode = (code && code.toLowerCase().indexOf(search.toLowerCase()) !== -1) || false;
      const cat = categorySet.size === 0 || categorySet.has(category)
      const cur = currencySet.size === 0 || currencySet.has(currency)
      const ris = riskSet.size === 0 || riskSet.has(risk.toString())
      return (searchName || searchCode) && cat && cur && ris
    })

    return { funds: filteredFunds, selected }
  }

  compare = (sorter: Sorter, asc: boolean) => (a: Fund, b: Fund): boolean => {
    const { data } = this.state
    if (sorter === 'checked') {
      const selected = new Set(data.map(({ id }) => id))
      return selected.has(a.id) && !selected.has(b.id)
    }
    const first = a[sorter] !== null ? a[sorter] : (asc ? Infinity : -Infinity)
    const second = b[sorter] !== null ? b[sorter] : (asc ? Infinity : -Infinity)

    // $FlowFixMe
    if (first < second)
      return asc
    return !asc
  }

  compareViennaByCategory = () => (a: Fund, b: Fund): boolean => {
    if(a['segmentOrder'] > b['segmentOrder']) return false;
    if(a['segmentOrder'] < b['segmentOrder']) return true;

    if(a['groupOrder'] > b['groupOrder']) return false;
    if(a['groupOrder'] < b['groupOrder']) return true;

    if(a['fundOrder'] > b['fundOrder']) return false;
    if(a['fundOrder'] < b['fundOrder']) return true;

    if(a['id'] > b['id']) return false;
    if(a['id'] < b['id']) return true;
    return true
  }

  compareCompensaByCategory = () => (a: Fund, b: Fund): boolean => {
    if(a['categoryOrder'] > b['categoryOrder']) return false;
    if(a['categoryOrder'] < b['categoryOrder']) return true;

    if(a['name'] > b['name']) return false;
    if(a['name'] < b['name']) return true;
    return true
  }

  manageGrouping = (grouped: 0 | 1) => {
    const { funds, sorter, asc } = this.state
    const { mode } = this
    if (mode === 'vienna') {
      grouped && mergesort(funds, this.compareViennaByCategory())
      !grouped && sorter && mergesort(funds, this.compare(sorter, asc))
    } else if(mode === 'compensa') {
      grouped && mergesort(funds, this.compareCompensaByCategory())
      !grouped && sorter && mergesort(funds, this.compare(sorter, asc))
    } else {
      grouped && mergesort(funds, this.compare('category', true))
      !grouped && sorter && mergesort(funds, this.compare(sorter, asc))
    }
    this.setState({ grouped })
  }

  sort = (sorter: Sorter) => (e: SyntheticEvent<>): void => {
    const { sorter: prevSorter, asc: prevAsc, funds, grouped } = this.state
    const asc = prevSorter === sorter ? !prevAsc : false
    mergesort(funds, this.compare(sorter, asc))
    grouped && mergesort(funds, this.compare('category', true))
    this.setState({ sorter, asc })
  }

  render () {
    const { categories, currencies, risks, activeFilters, data, sorter, lang, display, grouped, asc, date, loading } = this.state
    const { filters = true, fundList = true, chart = true, buttonGroup = true, legend = true } = display
    const { dateRange, limit, urlTemplate, urlComponents: { apiUrl }, appId, mode } = this
    const { size: { width } } = this.props
    const initial = this.props.initial
    return (
      <div id={appId} className="ui one column grid ac-app">
        <div className="row">
          <div className="column">
            {(chart || buttonGroup || legend)
            && <ChartContainer
              appId={appId}
              data={data}
              initial = {initial}
              activeFilters={activeFilters}
              urlTemplate={urlTemplate}
              initDateRange={dateRange}
              display={{ chart, buttonGroup, legend }}
              width={width}
              mode={mode}
              lang={lang}
            />}
          </div>
        </div>
        <div className="row">
          <div className="column" style={{"padding-left": 0}}>
            {filters && <Filters
              categories={categories}
              currencies={currencies}
              risks={risks}
              activeFilters={activeFilters}
              handleSelectInput={this.saveFilterChanges}
              handleSearchInput={this.saveSearchInput}
              clearSearchInput={this.clearSearchInput}
              width={width}
              lang={lang}
              mode={mode}
            />}
          </div>
        </div>
        <div className="row">
          <div className="column" style={{"padding-left": 0}}>
            {fundList && !loading && <List
              {...this.filterFunds()}
              changeDate={this.changeDate}
              limitReached={data.length >= limit}
              addData={this.addData}
              removeData={this.removeData}
              sort={this.sort}
              manageGrouping={this.manageGrouping}
              sorter={sorter}
              asc={asc}
              lang={lang}
              grouped={grouped}
              date={date}
              apiUrl={apiUrl}
              width={width}
              mode={mode}
            />}
            {loading && <Loader />}
          </div>
        </div>
        <ScrollerUp appAnchor={`#${appId}`} offset={230}/>
      </div>
    )
  }
}

export default sizeMe()(App)



// WEBPACK FOOTER //
// ./src/App.js